import { useLocation } from 'react-router';
import { Link, useNavigate } from '../i18n/routing';
import TrainZillaHeaderLogo from './TrainZillaHeaderLogo';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Mail, Twitter, Instagram, Linkedin, Globe, Smartphone, Youtube, ExternalLink, Facebook, Send, CheckCircle2, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { PLATFORM_METRICS } from '../config/metrics';
import { trackLead } from '../services/analytics';
import { COUNTRY_PAGES } from '../data/countryPages';
import { tr } from '../i18n/text';
import { useLeadGuard } from '../hooks/useLeadGuard';

const AppleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const PlayIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
  </svg>
);

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
  </svg>
);

// Simple Icons "Product Hunt" glyph (CC0).
const ProductHuntIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M13.604 8.4h-3.405V12h3.405c.995 0 1.801-.806 1.801-1.801 0-.993-.805-1.799-1.801-1.799zM12 0C5.372 0 0 5.372 0 12s5.372 12 12 12 12-5.372 12-12S18.628 0 12 0zm1.604 14.4h-3.405V18H7.801V6h5.804c2.319 0 4.2 1.88 4.2 4.199 0 2.321-1.881 4.201-4.201 4.201z"/>
  </svg>
);

const NewsletterForm = () => {
  const guard = useLeadGuard();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setStatus('loading');
    // Lazy-load the lead service — Footer renders on every page, so keep it
    // out of the main bundle for visitors who never touch this form.
    const { databaseService } = await import('../services/database');
    const result = await databaseService.subscribeToNewsletter(email, guard.read());

    if (result.success) {
      setStatus('success');
      trackLead({ form_id: 'newsletter', method: 'footer_newsletter', value: 1, lead_type: 'newsletter' });
      setMessage(result.message || 'Successfully subscribed!');
      setEmail('');
    } else {
      setStatus('error');
      setMessage(result.error || 'Something went wrong.');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex items-center gap-2 text-emerald-500 text-xs font-medium py-2 animate-in fade-in slide-in-from-left-2">
        <CheckCircle2 className="w-4 h-4" />
        {message}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="relative group">
{guard.field}
      <input
        type="email"
        placeholder={tr("Enter your email")}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        disabled={status === 'loading'}
        className="w-full pl-4 pr-12 py-2.5 rounded-xl text-xs transition-all duration-200 outline-none"
        style={{
          backgroundColor: 'var(--bg-tertiary)',
          color: 'var(--text-primary)',
          border: status === 'error' ? '1px solid #ef4444' : '1px solid var(--border-color)',
        }}
        onFocus={(e) => {
          if (status !== 'error') {
            e.target.style.borderColor = 'var(--accent-indigo)';
            e.target.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.12)';
          }
        }}
        onBlur={(e) => {
          if (status !== 'error') {
            e.target.style.borderColor = 'var(--border-color)';
            e.target.style.boxShadow = 'none';
          }
        }}
      />
      <button
        type="submit"
        disabled={status === 'loading'}
        className="absolute right-1.5 top-1.5 bottom-1.5 px-2.5 rounded-lg flex items-center justify-center transition-all duration-200 disabled:opacity-50"
        style={{
          backgroundColor: 'var(--accent-indigo)',
          color: 'white',
        }}
      >
        {status === 'loading' ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Send className="w-3.5 h-3.5" />
        )}
      </button>
      {status === 'error' && (
        <p className="text-[10px] text-red-500 mt-1.5 ml-1">{message}</p>
      )}
    </form>
  );
};

export default function Footer() {
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme } = useTheme();
  const { t } = useLanguage();
  const isDark = resolvedTheme === 'dark';

  const isCompactFooter = ['/login', '/register', '/dashboard'].some(p =>
    location.pathname === p || location.pathname.startsWith(p + '/')
  );

  const platformLinks: { name: string; href: string; external?: boolean }[] = [
    { name: t('common.navigation.platformOverview', { defaultValue: 'Platform Overview' }), href: '/solutions' },
    { name: tr("All features"), href: '/features' },
    { name: tr("✨ AI Agent Integration"), href: '/solutions/ai-agent' },
    { name: tr("🤖 AI Fitness Coach"), href: '/ai-coach' },
    { name: t('common.navigation.clientManagement', { defaultValue: 'Client Management' }), href: '/solutions/client-management' },
    { name: t('common.navigation.workoutPlanning', { defaultValue: 'Workout Planning' }), href: '/solutions/workout-planning' },
    { name: t('common.navigation.progressTracking', { defaultValue: 'Progress Tracking' }), href: '/solutions/progress-tracking' },
    { name: t('common.navigation.aiFeatures', { defaultValue: 'AI Features' }), href: '/solutions/ai-features' },
    { name: t('common.navigation.businessAnalytics', { defaultValue: 'Business Analytics' }), href: '/solutions/business-analytics' },
    { name: t('common.navigation.freeClientApp', { defaultValue: 'Free Client App' }), href: '/client-app' },
    { name: t('common.navigation.marketplace', { defaultValue: 'TrainZilla Marketplace' }), href: '/marketplace' },
  ];

  const solutionsLinks = [
    { name: t('common.navigation.personalTrainers', { defaultValue: 'Personal Trainers' }), href: '/benefits/personal-trainers' },
    { name: t('common.navigation.fitnessCenters', { defaultValue: 'Fitness Centers' }), href: '/benefits/fitness-centers' },
    { name: t('common.navigation.yogaInstructors', { defaultValue: 'Yoga Instructors' }), href: '/benefits/yoga-instructors' },
    { name: t('common.navigation.nutritionists', { defaultValue: 'Nutritionists' }), href: '/benefits/nutritionists' },
    { name: t('common.navigation.corporateWellness', { defaultValue: 'Corporate Wellness' }), href: '/benefits/corporate-wellness' },
    { name: t('common.navigation.pricingAndPlans', { defaultValue: 'Pricing & Plans' }), href: '/pricing' },
  ];

  const resourcesLinks = [
    { name: t('common.navigation.helpCenter', { defaultValue: 'Help Center' }), href: '/help-center' },
    { name: t('common.navigation.successStories', { defaultValue: 'Success Stories' }), href: '/success-stories' },
    { name: t('common.navigation.blog', { defaultValue: 'Blog' }), href: '/blog' },
    { name: t('common.navigation.webinars', { defaultValue: 'Webinars' }), href: '/webinars' },
    { name: t('common.navigation.compare', { defaultValue: 'Compare' }), href: '/compare' },
    { name: tr('Software Pricing Comparison'), href: '/compare/personal-trainer-software-pricing' },
    { name: tr('Trainerize Alternative'), href: '/alternatives/trainerize' },
    { name: tr('Everfit Alternative'), href: '/alternatives/everfit' },
    { name: tr('White Label Fitness App'), href: '/white-label-fitness-app' },
    { name: t('common.navigation.contactSupport', { defaultValue: 'Contact Support' }), href: '/contact-support' },
  ];

  const legalLinks = [
    { name: t('common.footer.privacyPolicy', { defaultValue: 'Privacy Policy' }), href: '/privacy-policy' },
    { name: t('common.footer.termsOfService', { defaultValue: 'Terms of Service' }), href: '/terms-of-service' },
    { name: t('common.footer.refundPolicy', { defaultValue: 'Refund Policy' }), href: '/refund-policy' },
    { name: tr('Sub-processors & DPA'), href: '/subprocessors' },
  ];

  const socialLinks = [
    { icon: <WhatsAppIcon className="w-4 h-4" />, href: 'https://wa.me/918459691471', label: tr("WhatsApp") },
    { icon: <Twitter className="w-4 h-4" />, href: 'https://x.com/trainzilla_in', label: tr("X (Twitter)") },
    { icon: <Instagram className="w-4 h-4" />, href: 'https://www.instagram.com/trainzilla.in', label: tr("Instagram") },
    { icon: <Facebook className="w-4 h-4" />, href: 'https://www.facebook.com/people/Trainzillain/61578776257338/', label: tr("Facebook") },
    { icon: <Linkedin className="w-4 h-4" />, href: 'https://www.linkedin.com/company/trainzilla', label: tr("LinkedIn") },
    { icon: <Youtube className="w-4 h-4" />, href: 'https://www.youtube.com/@trainzilla_in', label: tr("YouTube") },
    { icon: <ProductHuntIcon className="w-4 h-4" />, href: 'https://www.producthunt.com/products/trainzilla', label: tr("Product Hunt") },
  ];

  if (isCompactFooter) {
    return (
      <footer className="py-6 border-t" style={{ borderColor: 'var(--border-color)', backgroundColor: 'var(--bg-secondary)' }}>
        <div className="container-responsive flex flex-col sm:flex-row items-center justify-between gap-4">
          <div onClick={() => navigate('/')} className="cursor-pointer">
            <TrainZillaHeaderLogo variant={isDark ? 'white' : 'primary'} className="h-7 w-auto" />
          </div>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            © {new Date().getFullYear()} {t('common.footer.allRightsReserved', { defaultValue: 'TrainZilla Technologies. All rights reserved.' })}
          </p>
          <div className="flex gap-4">
            {legalLinks.slice(0, 2).map(l => (
              <Link key={l.href} to={l.href} className="text-xs transition-colors hover:text-indigo-500" style={{ color: 'var(--text-secondary)' }}>
                {l.name}
              </Link>
            ))}
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer style={{ backgroundColor: 'var(--bg-secondary)', borderTop: '1px solid var(--border-color)' }}>
      <div className="container-responsive py-16">
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-12">

          {/* Brand column */}
          <div className="lg:col-span-2">
            <div onClick={() => { navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer inline-block mb-5">
              <TrainZillaHeaderLogo variant={isDark ? 'white' : 'primary'} className="h-9 w-auto" />
            </div>
            <p className="text-sm leading-relaxed mb-5 max-w-xs" style={{ color: 'var(--text-secondary)' }}>
              {t('common.footer.brandText', { defaultValue: `The AI-powered platform helping ${PLATFORM_METRICS.TRAINERS} early adopter fitness coaches automate plan updates, manage clients, and scale their business.` })}
            </p>

            {/* Social Links */}
            <div className="flex gap-2 mb-8">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all duration-200 hover:-translate-y-0.5"
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                    color: 'var(--text-secondary)',
                    border: '1px solid var(--border-color)'
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.color = 'var(--accent-indigo)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent-indigo)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-color)';
                  }}
                >
                  {s.icon}
                </a>
              ))}
            </div>

            {/* Newsletter Subscription */}
            <div className="mb-8 max-w-xs">
              <h3 className="text-sm font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-secondary)' }}>
                {t('common.footer.newsletter', { defaultValue: 'Stay Updated' })}
              </h3>
              <p className="text-xs mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.8 }}>
                {t('common.footer.newsletterText', { defaultValue: 'Get the latest fitness business tips and product updates.' })}
              </p>
              <NewsletterForm />
            </div>

            {/* Coach App Download */}
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: 'var(--text-secondary)' }}>
                {t('common.actions.coachApp', { defaultValue: 'Coach App' })}
              </p>
              <div className="flex flex-col gap-2">
                <a
                  href="https://apps.apple.com/us/app/trainzilla-coach/id6760616851"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5 group"
                  style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
                >
                  <div style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                    <AppleIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.9 }}>{t('common.actions.downloadOn', { defaultValue: 'Download on the' })}</div>
                    <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('common.actions.appStore', { defaultValue: 'App Store' })}</div>
                  </div>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=in.trainzilla.coach"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 hover:-translate-y-0.5"
                  style={{ backgroundColor: 'var(--bg-tertiary)', border: '1px solid var(--border-color)' }}
                >
                  <div style={{ color: 'var(--text-secondary)', flexShrink: 0 }}>
                    <PlayIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-xs" style={{ color: 'var(--text-secondary)', opacity: 0.9 }}>{t('common.actions.getItOn', { defaultValue: 'Get it on' })}</div>
                    <div className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{t('common.actions.googlePlay', { defaultValue: 'Google Play' })}</div>
                  </div>
                </a>
              </div>
            </div>
          </div>

          {/* Platform */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-secondary)' }}>{t('common.footer.platform', { defaultValue: 'Platform' })}</h3>
            <ul className="space-y-2.5">
              {platformLinks.map(l => (
                <li key={l.href}>
                  {l.external ? (
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm transition-colors duration-150 hover:text-indigo-500"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {l.name}
                      <ExternalLink className="w-3 h-3 opacity-50" />
                    </a>
                  ) : (
                    <Link
                      to={l.href}
                      className="text-sm transition-colors duration-150 hover:text-indigo-500"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      {l.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Solutions */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-secondary)' }}>{t('common.footer.solutions', { defaultValue: 'Solutions' })}</h3>
            <ul className="space-y-2.5">
              {solutionsLinks.map(l => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-sm transition-colors duration-150 hover:text-indigo-500"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-secondary)' }}>{t('common.footer.resources', { defaultValue: 'Resources' })}</h3>
            <ul className="space-y-2.5">
              {resourcesLinks.map(l => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-sm transition-colors duration-150 hover:text-indigo-500"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-widest mb-4" style={{ color: 'var(--text-secondary)' }}>{t('common.footer.company', { defaultValue: 'Company' })}</h3>
            <ul className="space-y-2.5">
              <li>
                <Link to="/about-us" className="text-sm transition-colors duration-150 hover:text-indigo-500" style={{ color: 'var(--text-secondary)' }}>
                  {t('common.navigation.aboutUs', { defaultValue: 'About Us' })}
                </Link>
              </li>
              <li>
                <a href="mailto:support@trainzilla.in" className="text-sm flex items-center gap-2 transition-colors duration-150 hover:text-indigo-500" style={{ color: 'var(--text-secondary)' }}>
                  <Mail className="w-3.5 h-3.5 flex-shrink-0" />
                  {tr("support@trainzilla.in")}
                </a>
              </li>
              <li>
                <span className="text-sm flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                  <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                  {t('common.footer.global', { defaultValue: 'Worldwide · priced in INR and USD' })}
                </span>
              </li>
              <li className="text-sm flex flex-wrap gap-x-2 gap-y-1" style={{ color: 'var(--text-secondary)' }}>
                {COUNTRY_PAGES.map((c) => (
                  <Link key={c.code} to={c.path} className="hover:text-indigo-500 transition-colors duration-150">
                    {tr(c.name.replace(/^the /, ''))}
                  </Link>
                ))}
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid var(--border-color)' }}>
        <div className="container-responsive py-5 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
            © {new Date().getFullYear()} {t('common.footer.allRightsReserved', { defaultValue: 'TrainZilla Technologies. All rights reserved.' })}
          </p>
          <div className="flex flex-wrap gap-x-5 gap-y-1 justify-center">
            {legalLinks.map(l => (
              <Link key={l.href} to={l.href} className="text-xs transition-colors duration-150 hover:text-indigo-500" style={{ color: 'var(--text-muted)' }}>
                {l.name}
              </Link>
            ))}
            {/* EU/EEA/UK visitors can change their cookie choice (banner + flags live in index.html). */}
            {typeof window !== 'undefined' && (window as any).tzNeedsConsent && (
              <button
                type="button"
                onClick={() => (window as any).tzOpenCookieSettings?.()}
                className="text-xs transition-colors duration-150 hover:text-indigo-500"
                style={{ color: 'var(--text-muted)' }}
              >
                {t('common.footer.cookieSettings', { defaultValue: 'Cookie settings' })}
              </button>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
}